import { QueryClient, QueryFunction } from '@tanstack/react-query'
import { getMockData } from '@/test/mock-data'
import { getApiAuthToken } from '@/components/auth/api-auth-token'
import { getApiBaseUrl } from './api-paths'
import { isMockBackend } from '@/lib/mock-backend'

/** Excursion dashboard / ticket APIs always use the real backend, even when mock mode is on. */
function isV2ExcursionsApiPath(path: string): boolean {
  const p = path.startsWith('/') ? path : `/${path}`
  return p.split('?')[0].startsWith('/api/v2/excursions')
}

/** Auth headers for requests: in mock mode, skip Bearer unless calling a real-API bypass route. */
async function authHeadersForRequestPath(requestPath: string): Promise<Record<string, string>> {
  if (isMockBackend && !isV2ExcursionsApiPath(requestPath)) {
    return {}
  }
  const headers: Record<string, string> = {}
  const token = getApiAuthToken()
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return headers
}

/** Thrown by `apiRequest` when the HTTP status is not ok; carries `status` for UI messaging. */
export class ApiHttpError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiHttpError'
    this.status = status
  }
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText
    let message = text
    try {
      const json = JSON.parse(text) as { detail?: string | { msg?: string }[] }
      if (typeof json.detail === 'string') {
        message = json.detail
      } else if (Array.isArray(json.detail)) {
        message = json.detail
          .map((e) => (e && typeof e === 'object' && 'msg' in e ? e.msg : String(e)))
          .join('; ')
      }
    } catch {
      message = `${res.status}: ${text}`
    }
    throw new ApiHttpError(res.status, message)
  }
}

export async function getAuthHeaders(): Promise<Record<string, string>> {
  return authHeadersForRequestPath('')
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  options?: { skipErrorCheck?: boolean; signal?: AbortSignal },
): Promise<Response> {
  // Extract path from URL (handle both full URLs and relative paths)
  let path = url
  try {
    const urlObj = new URL(url)
    path = urlObj.pathname + urlObj.search
  } catch (err) {
    console.error('Parse API base URL', err)
  }

  // In dev mode, return mock data (except v2 excursions — always hit real API)
  if (isMockBackend && !isV2ExcursionsApiPath(path)) {
    const mockResponse = getMockData(path, method, data)

    // If it's already a Response (e.g., for SSE streams), return it
    if (mockResponse instanceof Response) {
      return mockResponse
    }

    // If it's a Blob (e.g., for file downloads), return it as a Response
    if (mockResponse instanceof Blob) {
      return new Response(mockResponse, {
        status: 200,
        headers: { 'Content-Type': mockResponse.type || 'application/octet-stream' },
      })
    }

    // Otherwise, return JSON response
    return new Response(JSON.stringify(mockResponse), {
      status: 200,
      headers: { 'Content-Type': 'application/json' },
    })
  }

  const headers: HeadersInit = await authHeadersForRequestPath(path)

  if (data) {
    headers['Content-Type'] = 'application/json'
  }

  const res = await fetch(url, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: 'include',
    signal: options?.signal,
  })

  // Skip error check for streaming responses (e.g., SSE) where we need to read the body as a stream
  if (!options?.skipErrorCheck) {
    await throwIfResNotOk(res)
  }
  return res
}

type UnauthorizedBehavior = 'returnNull' | 'throw'
export const getQueryFn: <T>(options: { on401: UnauthorizedBehavior }) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    // Join query key segments to form the path
    const path = queryKey.join('/') as string

    // In dev mode, return mock data (except v2 excursions)
    if (isMockBackend && !isV2ExcursionsApiPath(path)) {
      // Simulate network delay
      await new Promise((resolve) => setTimeout(resolve, 300))
      // Get mock data
      const mockData = getMockData(path, 'GET')
      // If query key has options (e.g., for output files), handle them
      if (queryKey.length > 1 && typeof queryKey[queryKey.length - 1] === 'object') {
        // For output files with options, we might need to filter
        // For now, just return the mock data
      }
      return mockData
    }

    // Build full URL from path
    const baseUrl = getApiBaseUrl()
    const url = `${baseUrl}${path}`

    const headers = await authHeadersForRequestPath(path)

    const res = await fetch(url, {
      headers,
      credentials: 'include',
    })

    if (unauthorizedBehavior === 'returnNull' && res.status === 401) {
      return null
    }

    await throwIfResNotOk(res)
    return res.json()
  }

/** Cache options for data that changes infrequently (workflow types, products, lots, reports, etc.) */
export const STABLE_QUERY_CACHE = {
  staleTime: 5 * 60 * 1000, // 5 min – consider data fresh, avoid refetch
  gcTime: 30 * 60 * 1000, // 30 min – keep in cache when unmounted
} as const

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: 'throw' }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
})
